import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useUpdateSponsorship } from "@/api/sponsorships";
import { currencies } from "@/lib/constants";
import PropTypes from "prop-types";
import { PhoneInput } from "@/components/ui/phone-input";
import {
  DEFAULT_COUNTRY_CODE,
  formatFullNumber,
  parsePhoneNumber,
} from "@/lib/country-codes";

function splitStored(storedFull, storedCode) {
  if (storedCode) {
    let national = (storedFull || "").replace(/[^\d]/g, "");
    const codeDigits = storedCode.replace(/\D/g, "");
    if ((storedFull || "").trim().startsWith("+") && national.startsWith(codeDigits)) {
      national = national.slice(codeDigits.length);
    }
    return { countryCode: storedCode, nationalNumber: national };
  }
  return parsePhoneNumber(storedFull || "", DEFAULT_COUNTRY_CODE);
}

// Currencies allowed by the Sponsorship model
const CURRENCY_OPTIONS = [
  "INR",
  "SAR",
  "QAR",
  "AED",
  "KWD",
  "OMR",
  "BHD",
  "MYR",
];

export function EditSponsorshipModal({ isOpen, onClose, sponsorship }) {
  const { mutate: updateSponsorship, isPending } = useUpdateSponsorship();

  const [form, setForm] = useState({
    sponsorName: "",
    sponsorPhoneCountryCode: DEFAULT_COUNTRY_CODE,
    sponsorPhone: "",
    place: "",
    mahalluName: "",
    amountPromised: "",
    currency: "INR",
    careOfName: "",
    careOfPhoneCountryCode: DEFAULT_COUNTRY_CODE,
    careOfPhone: "",
  });

  // Populate form when sponsorship changes
  useEffect(() => {
    if (sponsorship) {
      const main = splitStored(
        sponsorship.sponsorPhone || "",
        sponsorship.sponsorPhoneCountryCode,
      );
      const care = splitStored(
        sponsorship.careOf?.phone || "",
        sponsorship.careOf?.phoneCountryCode,
      );
      setForm({
        sponsorName: sponsorship.sponsorName || "",
        sponsorPhoneCountryCode: main.countryCode,
        sponsorPhone: main.nationalNumber,
        place: sponsorship.place || "",
        mahalluName: sponsorship.mahalluName || "",
        amountPromised: String(sponsorship.amountPromised || ""),
        currency: sponsorship.currency || "INR",
        careOfName: sponsorship.careOf?.name || "",
        careOfPhoneCountryCode: care.countryCode,
        careOfPhone: care.nationalNumber,
      });
    }
  }, [sponsorship]);

  if (!sponsorship) return null;

  const isIndividual = sponsorship.type === "Individual";
  const sym = currencies[form.currency || "INR"]?.symbol || "₹";

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSelectChange = (field) => (value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();

    const sponsorPhoneFull = form.sponsorPhone
      ? formatFullNumber(form.sponsorPhoneCountryCode, form.sponsorPhone)
      : undefined;
    const careOfFull = form.careOfPhone
      ? formatFullNumber(form.careOfPhoneCountryCode, form.careOfPhone)
      : undefined;

    const payload = {
      id: sponsorship._id,
      sponsorName: form.sponsorName.trim(),
      sponsorPhone: sponsorPhoneFull,
      sponsorPhoneCountryCode: form.sponsorPhone
        ? form.sponsorPhoneCountryCode
        : undefined,
      amountPromised: parseInt(form.amountPromised, 10),
      currency: form.currency,
      careOf: {
        name: form.careOfName.trim() || undefined,
        phone: careOfFull,
        phoneCountryCode: form.careOfPhone
          ? form.careOfPhoneCountryCode
          : undefined,
      },
    };

    if (isIndividual) {
      payload.place = form.place.trim();
    } else {
      payload.mahalluName = form.mahalluName.trim();
    }

    updateSponsorship(payload, {
      onSuccess: () => onClose(),
    });
  };

  const isValid =
    form.sponsorName.trim() &&
    form.amountPromised &&
    parseInt(form.amountPromised, 10) > 0 &&
    (isIndividual ? form.place.trim() : form.mahalluName.trim());

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Sponsorship</DialogTitle>
          <DialogDescription>
            Editing{" "}
            <span className="font-semibold">{sponsorship.sponsorshipId}</span> ·{" "}
            {sponsorship.type}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Sponsor Name */}
          <div className="space-y-1.5">
            <Label>Sponsor Name</Label>
            <Input
              value={form.sponsorName}
              onChange={handleChange("sponsorName")}
              placeholder="Sponsor name"
            />
          </div>

          {/* Phone */}
          <div className="space-y-1.5">
            <Label>Phone (optional)</Label>
            <PhoneInput
              value={form.sponsorPhone}
              onChange={(v) =>
                setForm((prev) => ({ ...prev, sponsorPhone: v }))
              }
              countryCode={form.sponsorPhoneCountryCode}
              onCountryCodeChange={(v) =>
                setForm((prev) => ({ ...prev, sponsorPhoneCountryCode: v }))
              }
              placeholder="Phone number"
              inputClassName="h-9"
              codeClassName="h-9"
            />
          </div>

          {/* Place or Mahallu */}
          {isIndividual ? (
            <div className="space-y-1.5">
              <Label>Place</Label>
              <Input
                value={form.place}
                onChange={handleChange("place")}
                placeholder="Place"
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Mahallu Name</Label>
              <Input
                value={form.mahalluName}
                onChange={handleChange("mahalluName")}
                placeholder="Mahallu name"
                disabled
              />
              <p className="text-xs text-muted-foreground">
                Mahallu name cannot be changed here; edit the chapter mahallu
                list instead.
              </p>
            </div>
          )}

          {/* Care Of */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>C/O Name</Label>
              <Input
                value={form.careOfName}
                onChange={handleChange("careOfName")}
                placeholder="Care of name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>C/O Phone</Label>
              <PhoneInput
                value={form.careOfPhone}
                onChange={(v) =>
                  setForm((prev) => ({ ...prev, careOfPhone: v }))
                }
                countryCode={form.careOfPhoneCountryCode}
                onCountryCodeChange={(v) =>
                  setForm((prev) => ({ ...prev, careOfPhoneCountryCode: v }))
                }
                placeholder="Care of phone"
                inputClassName="h-9"
                codeClassName="h-9"
              />
            </div>
          </div>

          {/* Amount Promised */}
          <div className="space-y-1.5">
            <Label>Amount Promised</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                min={sponsorship.paidAmount || 1}
                value={form.amountPromised}
                onChange={handleChange("amountPromised")}
                placeholder="Amount"
                className="flex-1"
              />
              <Select
                value={form.currency}
                onValueChange={handleSelectChange("currency")}
              >
                <SelectTrigger className="w-[90px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c} ({currencies[c]?.symbol || c})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {sponsorship.paidAmount > 0 && (
              <p className="text-xs text-muted-foreground">
                Min. amount: {sym}
                {sponsorship.paidAmount.toLocaleString()} (already collected)
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={isPending || !isValid}
            >
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

EditSponsorshipModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sponsorship: PropTypes.object,
};
